import clsx from "clsx";
import { forwardRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import OverlayTrigger from "../overlaytrigger";
import ColorInput, { validHex } from "./colorInput";
import ColorPickerIcon from "./colorPickerIcon";
import ColorPickerPopup, { ColorPickerPopupProps } from "./colorPickerPopup";

export interface ColorPickerProps {
  size?: "sm" | "md" | "lg";
  value?: string;
  onChange?: (color: string) => void;
  pickerProps?: Omit<ColorPickerPopupProps, "color" | "onChange" | "onCancel">;
}

const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  (props, ref) => {
    const { size = "md", value, onChange, pickerProps = {} } = props;
    const stopPropagation = {
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    };
    const [focused, setFocused] = useState(false);

    return (
      <OverlayTrigger
        trigger="click"
        placement="bottom-end"
        showArrow={false}
        overlayRole="dialog"
        overlay={({ dismiss }) => (
          <ColorPickerPopup
            {...pickerProps}
            color={value}
            onChange={onChange}
            onCancel={dismiss}
          />
        )}
      >
        {({ opened }) => (
          <div
            className={twMerge(
              clsx(
                "flex items-center w-full transition-all",
                "bg-white border border-gunmetal-200 rounded-sm outline-hidden",
                {
                  "ring-primary/25 border-hunter-300 ring-[3px]":
                    opened || focused,
                }
              )
            )}
          >
            {validHex(value) && (
              <span
                {...stopPropagation}
                className="flex items-center justify-center rounded-s-sm pl-2"
              >
                <span
                  style={{ backgroundColor: value }}
                  className={clsx("rounded-sm border", {
                    "w-6 h-6": size === "lg",
                    "w-5 h-5": size === "md",
                    "w-4 h-4": size === "sm",
                  })}
                />
              </span>
            )}
            <ColorInput
              ref={ref}
              className={twMerge(
                clsx(
                  "focus-visible:outline-hidden flex-1 min-w-0 text-gunmetal-800 rounded-sm",
                  {
                    "text-sm py-1 px-2": size === "sm",
                    "text-base py-1.5 px-3": size === "md",
                    "text-xl py-2 px-4": size === "lg",
                  }
                )
              )}
              {...stopPropagation}
              color={value}
              onChange={onChange}
              onBlur={(e) => {
                if (!e.target.value || e.target.value === "#") onChange?.("");
                if (!opened) setFocused(false);
              }}
              onFocus={() => setFocused(true)}
            />
            <span className="flex items-center justify-center w-7 rounded-e-sm text-center hover:cursor-pointer text-gunmetal-600">
              <ColorPickerIcon size={15} />
            </span>
          </div>
        )}
      </OverlayTrigger>
    );
  }
);

export default ColorPicker;
